import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import { AsyncContentManager, PayloadBody, PayloadItem, PayloadItemData } from '@tk/utilities/tk.async.content.manager';
import '@scss/components/_async-content.scss';

export type AsyncContentTranslations = Record<string, string>;

export interface AsyncContentRefreshOptions {
    reason?: string;
}

export interface AsyncContentAttributeUpdateOptions extends AsyncContentRefreshOptions {
    refresh?: boolean;
}

export type AsyncContentAttributeValues = Record<string, string | number | boolean | null | undefined>;

export abstract class TKAsyncContent extends TKCustomElementFactory {
    private observer: IntersectionObserver | null = null;
    private attributeObserver: MutationObserver | null = null;
    private autoReloadTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private suppressNextAutoReload: boolean = false;

    constructor() {
        super();
    }

    // ─── Attribute accessors ──────────────────────────────────────────────────

    get tkId(): string | null {
        return this.getAttribute('data-tk-id');
    }

    get usecase(): string | null {
        return this.getAttribute('data-tk-usecase');
    }

    get url(): string | null {
        return this.getAttribute('data-tk-url');
    }

    get cacheStrategy(): string {
        return this.getAttribute('data-tk-cache-strategy') ?? 'session';
    }

    /** When present, the Intersection Observer is used for lazy loading. */
    get isLazy(): boolean {
        return this.hasAttribute('data-tk-is-lazy');
    }

    get rootMargin(): string {
        return this.getAttribute('data-tk-root-margin') ?? '200px';
    }

    get threshold(): number {
        const value = parseFloat(this.getAttribute('data-tk-threshold') ?? '0');
        return isNaN(value) ? 0 : value;
    }

    /**
     * Comma-separated list of attribute names that should trigger a refresh
     * when they change, e.g. "data-tk-quantity,data-tk-dimension-variant".
     */
    get autoReloadAttributes(): string[] {
        const source = this.getAttribute('data-tk-auto-reload-attrs') ?? '';
        return source
            .split(',')
            .map((value) => value.trim())
            .filter((value) => value.length > 0);
    }

    /** Debounce in ms for auto-reload attribute changes. */
    get autoReloadDebounceMs(): number {
        const value = parseInt(this.getAttribute('data-tk-auto-reload-debounce') ?? '0', 10);
        return isNaN(value) || value < 0 ? 0 : value;
    }

    // ─── Lifecycle ────────────────────────────────────────────────────────────

    connectedCallback(): void {
        if (!this.tkId || !this.url || !this.usecase) {
            console.warn('tk-async-content: data-tk-id, data-tk-url and data-tk-usecase are required.', this);
            return;
        }

        this._setupAutoReloadObserver();

        if (this.isLazy) {
            this._setupIntersectionObserver();
        } else {
            void this.loadContent();
        }
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        this.observer?.disconnect();
        this.observer = null;
        this.attributeObserver?.disconnect();
        this.attributeObserver = null;
        if (this.autoReloadTimeoutId !== null) {
            clearTimeout(this.autoReloadTimeoutId);
            this.autoReloadTimeoutId = null;
        }
    }

    // ─── Private helpers ──────────────────────────────────────────────────────

    private _setupIntersectionObserver(): void {
        this.observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        void this.loadContent();
                        this.observer?.disconnect();
                        break;
                    }
                }
            },
            {
                rootMargin: this.rootMargin,
                threshold: this.threshold,
            },
        );

        this.observer.observe(this);
    }

    private _renderElement(data: unknown): void {
        const templateNode = this.querySelector('template');

        if (templateNode) {
            // Declarative template interpolation via ${path.to.value} placeholders
            const templateContent = templateNode.innerHTML;
            this.innerHTML = this._interpolate(templateContent, data);
        } else {
            const rendered = this.render(data);
            if (rendered) {
                this.innerHTML = rendered;
            }
        }
    }

    private _setupAutoReloadObserver(): void {
        const watchedAttributes = this.autoReloadAttributes;
        if (watchedAttributes.length === 0) {
            return;
        }

        this.attributeObserver?.disconnect();
        this.attributeObserver = new MutationObserver((mutations) => {
            const changedWatchedAttribute = mutations.some((mutation) => {
                if (mutation.type !== 'attributes' || !mutation.attributeName) {
                    return false;
                }
                return watchedAttributes.includes(mutation.attributeName);
            });

            if (!changedWatchedAttribute) {
                return;
            }

            if (this.suppressNextAutoReload) {
                this.suppressNextAutoReload = false;
                return;
            }

            this._scheduleAutoReload('attribute-change');
        });

        this.attributeObserver.observe(this, {
            attributes: true,
            attributeFilter: watchedAttributes,
        });
    }

    private _scheduleAutoReload(reason: string): void {
        if (this.autoReloadTimeoutId !== null) {
            clearTimeout(this.autoReloadTimeoutId);
            this.autoReloadTimeoutId = null;
        }

        const debounceMs = this.autoReloadDebounceMs;
        if (debounceMs <= 0) {
            void this.refreshContent({ reason });
            return;
        }

        this.autoReloadTimeoutId = setTimeout(() => {
            this.autoReloadTimeoutId = null;
            void this.refreshContent({ reason });
        }, debounceMs);
    }

    /**
     * Replaces `${path.to.value}` placeholders in the template string
     * using dot-notation property access on the provided data object.
     */
    private _interpolate(template: string, data: unknown): string {
        return template.replace(/\$\{([^}]+)\}/g, (_, key: string) => {
            const path = key.trim().split('.');
            let value: unknown = data;
            for (const segment of path) {
                if (value === null || value === undefined) return '';
                value = (value as Record<string, unknown>)[segment];
            }
            return value !== undefined && value !== null ? String(value) : '';
        });
    }

    protected parseTranslationsJson(source: string | null, origin: string): AsyncContentTranslations {
        if (!source) {
            return {};
        }

        try {
            const parsed = JSON.parse(source) as unknown;

            if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
                console.warn('[TKAsyncContent] invalid translations payload', { origin, parsed });
                return {};
            }

            return Object.entries(parsed).reduce<AsyncContentTranslations>((translations, [key, value]) => {
                if (typeof value === 'string') {
                    translations[key] = value;
                }
                return translations;
            }, {});
        } catch (error) {
            console.warn('[TKAsyncContent] failed to parse translations JSON', { origin, error, source });
            return {};
        }
    }

    protected getTranslations(): AsyncContentTranslations {
        const translationsFromAttribute = this.parseTranslationsJson(
            this.getAttribute('data-tk-translations'),
            'data-tk-translations',
        );
        const translationsScript = this.querySelector<HTMLScriptElement>('script[data-tk-translations]');
        const translationsFromScript = this.parseTranslationsJson(
            translationsScript?.textContent?.trim() ?? null,
            'script[data-tk-translations]',
        );

        const translations = {
            ...translationsFromAttribute,
            ...translationsFromScript,
        };

        Array.from(this.attributes).forEach((attribute) => {
            if (!attribute.name.startsWith('data-tk-translation-')) {
                return;
            }

            const translationKey = attribute.name.replace('data-tk-translation-', '').replace(/-/g, '_');
            translations[translationKey] = attribute.value;
        });

        return translations;
    }

    protected getTranslation(key: string, fallback: string = ''): string {
        const translations = this.getTranslations();
        const normalizedKey = key.replace(/-/g, '_');
        return translations[key] ?? translations[normalizedKey] ?? fallback;
    }

    // ─── Overridable hooks ────────────────────────────────────────────────────

    /**
     * Override in subclasses to provide item-specific request parameters
     * (e.g. quantity, dimensionVariant, stockType).
     * The returned object is stored alongside the item in the batch queue and
     * forwarded to `buildPayload` when the request is sent.
     */
    protected getItemData(): PayloadItemData | undefined {
        return undefined;
    }

    /**
     * Override to separate batching queues for requests that share usecase/url
     * but require different batch-level payload options.
     */
    protected getBatchKey(): string | undefined {
        return undefined;
    }

    /**
     * Override to customize cache identity. By default, itemData is used.
     */
    protected getCacheKey(itemData: PayloadItemData | undefined): string | undefined {
        if (!itemData) {
            return undefined;
        }

        return this._stableStringify(itemData);
    }

    private _stableStringify(value: unknown): string {
        if (value === null || typeof value !== 'object') {
            return JSON.stringify(value);
        }

        if (Array.isArray(value)) {
            return `[${value.map((item) => this._stableStringify(item)).join(',')}]`;
        }

        const record = value as Record<string, unknown>;
        const keys = Object.keys(record).sort();
        return `{${keys.map((key) => `${JSON.stringify(key)}:${this._stableStringify(record[key])}`).join(',')}}`;
    }

    /**
     * Override in subclasses to assemble the POST body from all batched items.
     * Receives every `{ id, itemData }` pair collected during the debounce window.
     *
     * The default implementation sends a minimal `FormData` payload with `usecase` and `ids`
     * which can be used for simple, ID-only endpoints.
     */
    protected buildPayload(items: PayloadItem[]): PayloadBody {
        const formData = new FormData();

        if (this.usecase) {
            formData.append('usecase', this.usecase);
        }
        formData.append('ids', JSON.stringify(items.map((item) => item.id)));

        return formData;
    }

    // ─── Public API ───────────────────────────────────────────────────────────

    /**
     * Public refresh entrypoint for host integrations (e.g. basket counter updates).
     */
    public async refreshContent(options?: AsyncContentRefreshOptions): Promise<void> {
        this.dispatchEvent(
            new CustomEvent('tk-content-refresh-requested', {
                detail: {
                    reason: options?.reason ?? 'manual',
                },
                bubbles: true,
                composed: true,
            }),
        );

        await this.loadContent();
    }

    /**
     * Updates one or more attributes on the component and optionally refreshes.
     * Useful for integrations that calculate quantity/variant externally.
     */
    public async updateAttributesAndRefresh(
        updates: AsyncContentAttributeValues,
        options?: AsyncContentAttributeUpdateOptions,
    ): Promise<void> {
        const shouldRefresh = options?.refresh !== false;
        if (shouldRefresh) {
            this.suppressNextAutoReload = true;
        }

        Object.entries(updates).forEach(([attribute, value]) => {
            if (value === undefined || value === null) {
                this.removeAttribute(attribute);
                return;
            }

            this.setAttribute(attribute, String(value));
        });

        if (!shouldRefresh) {
            return;
        }

        await this.refreshContent({ reason: options?.reason ?? 'attribute-update' });
    }

    public async loadContent(): Promise<void> {
        if (!this.tkId || !this.url || !this.usecase) return;

        const itemData = this.getItemData();
        const batchKey = this.getBatchKey();
        const cacheKey = this.getCacheKey(itemData);

        this.classList.add('tk-async-content--is-loading');

        try {
            const manager = AsyncContentManager.getInstance();
            const data = await manager.requestContent({
                id: this.tkId,
                usecase: this.usecase,
                url: this.url,
                cacheStrategy: this.cacheStrategy,
                batchKey,
                cacheKey,
                itemData,
                buildPayload: (items: PayloadItem[]) => this.buildPayload(items),
            });

            this._renderElement(data);

            this.classList.remove('tk-async-content--is-loading');
            this.classList.add('tk-async-content--is-loaded');

            this.dispatchEvent(
                new CustomEvent('tk-content-loaded', {
                    detail: data,
                    bubbles: true,
                    composed: true,
                }),
            );
        } catch (error) {
            console.error(`tk-async-content: Error loading content for id="${this.tkId}":`, error);

            this.classList.remove('tk-async-content--is-loading');
            this.classList.add('tk-async-content--is-error');
            this.innerHTML = '<span class="tk-async-content__error-placeholder"></span>';
        }
    }

    /**
     * Abstract render method. Receives the raw JSON data returned by the API
     * and must return an HTML string (produced via TSX or plain string concatenation).
     *
     * Override this in subclasses to define the component-specific markup.
     */
    public abstract render(data: unknown): string;
}
